import React, { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { visitorsAPI, usersAPI, flatsAPI } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { 
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Paper, 
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Alert,
  useTheme, useMediaQuery, Card, CardContent, Chip, TableContainer
} from "@mui/material";
//import VisitorDialog from "./VisitorDialog";

// Cross-platform camera implementation
const initializeCamera = async () => {
  // Check if we're in a mobile app environment (Capacitor)
  if (window.Capacitor) {
    // Import Capacitor modules dynamically when available
    const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
    const { Capacitor } = await import('@capacitor/core');
    return {
      Camera,
      CameraResultType,
      CameraSource,
      Capacitor,
      isNative: true
    };
  } else {
    // Web implementation
    return {
      Camera: {
        checkPermissions: async () => ({ camera: 'granted' }),
        requestPermissions: async () => ({ camera: 'granted' }),
        getPhoto: async (options) => {
          throw new Error('Use web camera implementation instead');
        }
      },
      CameraResultType: {
        Uri: 'uri',
        Base64: 'base64',
        DataUrl: 'dataUrl'
      },
      CameraSource: {
        Camera: 'camera',
        Photos: 'photos'
      },
      Capacitor: {
        isNativePlatform: () => false
      },
      isNative: false
    };
  }
};

const VisitorDashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Determine user groups and permissions
  const groups = user?.groups || [];
  
  // Extract group names - handle both old format (strings) and new format (objects)
  const groupNames = groups.map(g => typeof g === 'string' ? g : g.name);
  
  // Check both groups and direct role property
  const isBuildingAdmin = groupNames.includes('building_admin') || user?.role === 'building_admin';
  const isMaintenanceStaff = groupNames.includes('maintenance_staff') || user?.role === 'maintenance_staff';
  const isOwner = groupNames.includes('owner') || user?.role === 'resident';
  const isTenant = groupNames.includes('tenant') || user?.role === 'resident';
  const canUseCamera = isBuildingAdmin || isMaintenanceStaff;
  const canUseLeaveAtGate = isOwner || isTenant;
  
  const [buildingId, setBuildingId] = useState("");
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addError, setAddError] = useState('');
  const [adding, setAdding] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingVisitor, setEditingVisitor] = useState(null);
  const [editError, setEditError] = useState('');
  const [editing, setEditing] = useState(false);
  const [flats, setFlats] = useState([]);
  const hasInitialized = useRef(false);

  // Camera platform state
  const [cameraAPI, setCameraAPI] = useState(null);
  const [cameraInitialized, setCameraInitialized] = useState(false);

  // Reusable function to refresh visitors data
  const refreshVisitors = useCallback(async (buildingIdToUse) => {
    if (!buildingIdToUse) return;
    try {
      const today = new Date();
      const dd = String(today.getDate()).padStart(2, "0");
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const yyyy = today.getFullYear();
      const dateStr = `${dd}-${mm}-${yyyy}`;
      console.log("VisitorDashboard: Refreshing visitors for building:", buildingIdToUse, "date:", dateStr);
      const res = await visitorsAPI.getByBuildingAndDate(buildingIdToUse, dateStr);
      console.log("VisitorDashboard: Visitors refresh response:", res);
      setVisitors(res.data || []);
    } catch (err) {
      console.error("VisitorDashboard: Error refreshing visitors:", err);
      setVisitors([]);
    }
  }, []);

  // Initialize camera platform
  useEffect(() => {
    const initCamera = async () => {
      try {
        const camera = await initializeCamera();
        setCameraAPI(camera);
        setCameraInitialized(true);
        console.log('Camera platform initialized:', camera.isNative ? 'Native' : 'Web');
      } catch (error) {
        console.error('Failed to initialize camera:', error);
        setCameraInitialized(false);
      }
    };
    
    initCamera();
  }, []);

  useEffect(() => {
    const fetchBuildingAssociation = async () => {
      const userId = user?.attributes?.sub;
      console.log("VisitorDashboard: userId =", userId);
      if (!userId) {
        console.log("VisitorDashboard: No userId found");
        setLoading(false);
        hasInitialized.current = false; // Reset when no user
        return;
      }
      
      // Prevent double loading for the same user
      if (hasInitialized.current) {
        console.log("VisitorDashboard: Already initialized, skipping");
        return;
      }
      
      try {
        hasInitialized.current = true;
        setLoading(true);
        console.log("VisitorDashboard: Fetching associations for userId:", userId);
        const res = await usersAPI.getAssociations(userId);
        const buildingAssoc = res.data || [];
        console.log("VisitorDashboard: Building associations:", buildingAssoc);
        const ids = buildingAssoc.map(item => item.buildingId);
        console.log("VisitorDashboard: Building IDs:", ids);
        
        if (ids && ids.length > 0) {
          setBuildingId(ids[0]);
          console.log("VisitorDashboard: Set buildingId to:", ids[0]);
          
          // Fetch flats data based on user role
          try {
            console.log("VisitorDashboard: Building associations:", buildingAssoc);
            let flatsData = [];
            
            if (isBuildingAdmin || isMaintenanceStaff) {
              // Building admin and maintenance staff can see all flats in the building
              const flatsRes = await flatsAPI.getByBuilding(ids[0]);
              flatsData = flatsRes.data || [];
              console.log("VisitorDashboard: Building admin/maintenance staff - flats loaded:", flatsData.length);
            } else {
              // For owners/tenants, use user associations to get their flats
              const userId = user?.attributes?.sub;
              
              // Get flat IDs from user associations
              const userFlatIds = buildingAssoc
                .filter(assoc => assoc.flatId) // Only associations with flatId
                .map(assoc => assoc.flatId);
              
             
              if (userFlatIds.length > 0) {
                // Fetch all flats and filter by user's associated flat IDs
                const flatsRes = await flatsAPI.getByBuilding(ids[0]);
                const allFlats = flatsRes.data || [];
                console.log("VisitorDashboard: All flats in building:", allFlats.length);
                
                flatsData = allFlats.filter(flat => userFlatIds.includes(flat.flatId));
                console.log("VisitorDashboard: Filtered flats by associations:", flatsData.length);
                
                // Debug the filtering
                flatsData.forEach((flat, index) => {
                  console.log(`VisitorDashboard: User's Flat ${index + 1}:`, {
                    flatId: flat.flatId,
                    flatNumber: flat.flatNumber,
                    blockName: flat.blockName
                  });
                });
              } else {
                console.log("VisitorDashboard: No flat IDs found in user associations");
                flatsData = [];
              }
            }
            
            setFlats(flatsData);
            console.log("VisitorDashboard: Final flats set:", flatsData);
          } catch (err) {
            console.error("VisitorDashboard: Error fetching flats:", err);
            setFlats([]);
          }
          
          // Fetch visitors data using the reusable function
          await refreshVisitors(ids[0]);
        } else {
          console.log("VisitorDashboard: No building IDs found");
          setBuildingId("");
          setVisitors([]);
          setFlats([]);
        }
      } catch (err) {
        console.error("VisitorDashboard: Error fetching building associations:", err);
        setBuildingId("");
        setVisitors([]);
        setFlats([]);
        hasInitialized.current = false; // Reset on error so it can retry
      } finally {
        setLoading(false);
      }
    };
    
    fetchBuildingAssociation();    
  }, [user?.attributes?.sub]);

  console.log("VisitorDashboard: Current state - buildingId:", buildingId, "flats:", flats, "visitors:", visitors, "loading:", loading, "isBuildingAdmin:", isBuildingAdmin, "isMaintenanceStaff:", isMaintenanceStaff);

  return (
    <Box className="mobile-content-spacing" sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        {t('visitor.title')}
      </Typography>
      {buildingId && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {(isBuildingAdmin || isMaintenanceStaff) ? `All Flats: ${flats.length}` : `My Flats: ${flats.length}`} | 
          {t('visitor.visitors')}: {visitors.length}
        </Typography>
      )}
      <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" onClick={() => setAddDialogOpen(true)}>
          {t('visitor.addVisitor')}
        </Button>
      </Box>
      {isMobile ? (
        // Mobile Card Layout
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
          {loading ? (
            <Alert severity="info" sx={{ fontSize: '14px' }}>{t('visitor.loading')}</Alert>
          ) : visitors.length === 0 ? (
            <Alert severity="info" sx={{ fontSize: '14px' }}>{t('visitor.noActiveVisitors')}</Alert>
          ) : (
            visitors.map((v) => (
              <Card key={v.visitorId} variant="outlined" sx={{ p: 1 }}>
                <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                      {v.visitorName}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Chip 
                        label={v.status} 
                        color={v.status === 'Checked In' ? 'success' : 'default'} 
                        size="small" 
                        sx={{ fontSize: '10px', height: '20px' }}
                      />
                      <Chip 
                        label={v.approvalStatus || "Pending"} 
                        color={v.approvalStatus === 'Approved' ? 'success' : 'warning'} 
                        size="small" 
                        sx={{ fontSize: '10px', height: '20px' }}
                      />
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary', mb: 0.5 }}>
                    Flat: {v.flatNumber}/{v.blockName}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary', mb: 0.5 }}>
                    Purpose: {v.purpose}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontSize: '11px' }}>
                      In: {v.inTime}
                    </Typography>
                    {v.outTime && (
                      <Typography variant="caption" sx={{ fontSize: '11px' }}>
                        Out: {v.outTime}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setEditingVisitor(v);
                        setEditDialogOpen(true);
                        setEditError('');
                      }}
                      sx={{ fontSize: '12px', px: 2 }}
                    >
                      Edit
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      ) : (
        // Desktop Table Layout
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('visitor.visitorName')}</TableCell>
                <TableCell>{t('visitor.flatBlock')}</TableCell>
                <TableCell>{t('visitor.purpose')}</TableCell>
                <TableCell>{t('visitor.checkInTime')}</TableCell>
                <TableCell>{t('visitor.outTime')}</TableCell>
                <TableCell>{t('visitor.status')}</TableCell>
                <TableCell>{t('visitor.approvalStatus')}</TableCell>
                <TableCell>{t('visitor.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8}>{t('visitor.loading')}</TableCell>
                </TableRow>
              ) : visitors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>{t('visitor.noActiveVisitors')}</TableCell>
                </TableRow>
              ) : (
                visitors.map((v) => (
                  <TableRow key={v.visitorId}>
                    <TableCell>{v.visitorName}</TableCell>
                    <TableCell>{v.flatNumber}/{v.blockName}</TableCell>
                    <TableCell>{v.purpose}</TableCell>
                    <TableCell>{v.inTime}</TableCell>
                    <TableCell>{v.outTime || ""}</TableCell>
                    <TableCell>{v.status}</TableCell>
                    <TableCell>{v.approvalStatus || t('visitor.pending')}</TableCell> {/* Show approvalStatus */}
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setEditingVisitor(v);
                          setEditDialogOpen(true);
                          setEditError('');
                        }}
                      >
                        {t('visitor.edit')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <VisitorDialog
        open={addDialogOpen}
        onClose={() => { 
          setAddDialogOpen(false); 
          setAddError(""); 
        }}
        onSubmit={async (data) => {
          setAdding(true);
          setAddError("");
          try {
            await visitorsAPI.create({
              ...data
            });
            setAddDialogOpen(false);
            // Refresh visitors using reusable function
            await refreshVisitors(buildingId);
          } catch (err) {
            setAddError("Failed to add visitor.");
          }
          setAdding(false);
        }}
        isLoading={adding}
        error={addError}
        buildingId={buildingId}
        flats={flats}
        canUseCamera={canUseCamera}
        canUseLeaveAtGate={canUseLeaveAtGate}
        cameraAPI={cameraAPI}
        cameraInitialized={cameraInitialized}
      />
      <VisitorDialog
        open={editDialogOpen}
        onClose={() => { setEditDialogOpen(false); setEditError(""); setEditingVisitor(null); }}
        onSubmit={async (data) => {
          setEditing(true);
          setEditError("");
          try {
            await visitorsAPI.update(buildingId, editingVisitor.visitorId || editingVisitor.visitorID, {
              ...editingVisitor,
              ...data, // <-- this includes the updated status and outTime
              buildingId,
            });
            setEditDialogOpen(false);
            setEditingVisitor(null);
            // Refresh visitors using reusable function
            await refreshVisitors(buildingId);
          } catch (err) {
            setEditError("Failed to update visitor.");
          }
          setEditing(false);
        }}
        isLoading={editing}
        error={editError}
        buildingId={buildingId}
        flats={flats}
        initialData={editingVisitor}
        canUseCamera={canUseCamera}
        canUseLeaveAtGate={canUseLeaveAtGate}
        cameraAPI={cameraAPI}
        cameraInitialized={cameraInitialized}
      />
    </Box>
  );
};

// --- VisitorDialog with robust camera preview ---
const VisitorDialog = ({
  open,
  onClose,
  onSubmit,
  isLoading,
  error,
  buildingId,
  flats,
  initialData,
  canUseCamera = false,
  canUseLeaveAtGate = false,
  cameraAPI,
  cameraInitialized,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  console.log("VisitorDialog: Props received - buildingId:", buildingId, "flats:", flats, "flats.length:", flats?.length);
  const getCurrentDateTimeLocal = () => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const hh = String(now.getHours()).padStart(2, "0");
    const min = String(now.getMinutes()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  };

  const [photo, setPhoto] = useState(null);
  const [photoError, setPhotoError] = useState('');
  const [notesError, setNotesError] = useState('');
  const [formData, setFormData] = useState(initialData || {
    visitorId: initialData?.visitorId || '',
    buildingId: buildingId || '',
    visitorName: "",
    flatId: "",
    flatNumber: "",
    blockName: "",
    purpose: "",
    phoneNumber: "",
    inTime: getCurrentDateTimeLocal(),
    status: "IN",
    approvalStatus: "PENDING",
    vehicleNumber: "",
    idType: "",
    idNumber: "",
    notes: ""
  });

  // Camera refs and state
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [stream, setStream] = useState(null);

  // Assign stream to video element when both are ready
  useEffect(() => {
    if (cameraActive && stream && videoRef.current) {
      const video = videoRef.current;
      video.srcObject = stream;
      
      // Enhanced mobile video handling
      video.playsInline = true;
      video.muted = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      
      // Handle video loading and playback
      const playVideo = async () => {
        try {
          await video.play();
        } catch (error) {
          console.warn('Video autoplay failed:', error);
          // This is common on mobile, user will need to interact first
        }
      };
      
      if (video.readyState >= 2) {
        playVideo();
      } else {
        video.addEventListener('loadeddata', playVideo, { once: true });
      }
    }
  }, [cameraActive, stream]);

  // Cleanup camera stream on dialog close or unmount
  useEffect(() => {
    if (!open && stream) {
      stream.getTracks().forEach(track => track.stop());
      setCameraActive(false);
      setStream(null);
    }
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
    // eslint-disable-next-line
  }, [open, stream]);

  // Reset photo when dialog closes
  useEffect(() => {
    if (!open) {
      setPhoto(null);
      setPhotoError('');
      setNotesError('');
    }
  }, [open]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        visitorId: initialData.visitorId || '',
        buildingId: initialData.buildingId || buildingId || '',
        visitorName: initialData.visitorName || '',
        flatId: initialData.flatId || initialData.flatID || '',
        flatNumber: initialData.flatNumber || '',
        blockName: initialData.blockName || '',
        purpose: initialData.purpose || '',
        phoneNumber: initialData.phoneNumber || '',
        inTime: initialData.inTime || getCurrentDateTimeLocal(),
        status: initialData.status || 'IN',
        approvalStatus: initialData.approvalStatus || 'PENDING',
        vehicleNumber: initialData.vehicleNumber || '',
        idType: initialData.idType || '',
        idNumber: initialData.idNumber || '',
        notes: initialData.notes || ''
      });
    } else if (open) {
      setFormData({
        visitorId: '',
        buildingId: buildingId || '',
        visitorName: "",
        flatId: "",
        flatNumber: "",
        blockName: "",
        purpose: "",
        phoneNumber: "",
        inTime: getCurrentDateTimeLocal(),
        status: "IN",
        approvalStatus: "PENDING",
        vehicleNumber: "",
        idType: "",
        idNumber: "",
        notes: ""
      });
      // Reset photo when dialog opens for new visitor
      setPhoto(null);
      setPhotoError('');
    }
  }, [initialData, open, buildingId]);

  const handleChange = (field) => (e) => {
    if (field === "flatId") {
      const selectedFlat = (flats || []).find(f => f.flatId === e.target.value);
      setFormData((prev) => ({
        ...prev,
        flatId: e.target.value,
        flatNumber: selectedFlat ? selectedFlat.flatNumber : "",
        blockName: selectedFlat ? selectedFlat.blockName : ""
      }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.purpose) {
      setNotesError("Purpose is required.");
      return;
    }
    if (formData.purpose === "Others" && !formData.notes.trim()) {
      setNotesError("Notes are required when purpose is Others.");
      return;
    }
    if (photoError) return;

    let payload = { ...formData };
    if (photo && typeof photo === "string") {
      payload.photo = photo;
    }
    // If status is OUT, set outTime to now
    if (formData.status === "OUT") {
      payload.outTime = new Date().toISOString();
    }
    setNotesError('');
    onSubmit(payload);
  };

  // Helper function to check camera permissions
  const checkCameraPermission = async () => {
    if (!cameraAPI) {
      console.warn('Camera API not initialized');
      return 'denied';
    }

    try {
      // For native apps, use Capacitor's permission system
      if (cameraAPI.Capacitor.isNativePlatform()) {
        const permission = await cameraAPI.Camera.checkPermissions();
        return permission.camera === 'granted' ? 'granted' : 'denied';
      }
      
      // For web, use browser permissions API
      if (navigator.permissions) {
        const permission = await navigator.permissions.query({ name: 'camera' });
        return permission.state;
      }
      return 'unknown';
    } catch (error) {
      console.warn('Permission check failed:', error);
      return 'unknown';
    }
  };

  // --- IMPROVED CAMERA LOGIC FOR NATIVE AND WEB ---
  const startCamera = async () => {
    if (!cameraAPI) {
      setPhotoError('Camera not available');
      return;
    }

    setPhotoError('');
    setCameraActive(true);
    
    try {
      // For native platforms, use Capacitor Camera
      if (cameraAPI.Capacitor.isNativePlatform()) {
        // Request permissions if needed
        const permission = await cameraAPI.Camera.checkPermissions();
        if (permission.camera !== 'granted') {
          const requested = await cameraAPI.Camera.requestPermissions();
          if (requested.camera !== 'granted') {
            throw new Error('Camera permission denied');
          }
        }
        
        // No need to start stream for native camera - it's handled by the capture function
        return;
      }

      // Web fallback - existing getUserMedia logic
      const permissionState = await checkCameraPermission();
      if (permissionState === 'denied') {
        throw new Error('Camera permission denied');
      }

      // Enhanced camera constraints for mobile devices
      const constraints = {
        video: {
          facingMode: 'environment', // Use back camera by default
          width: { ideal: 1280, max: 1920 },
          height: { ideal: 720, max: 1080 },
          aspectRatio: { ideal: 16/9 }
        },
        audio: false
      };

      // Check if device supports getUserMedia
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera not supported on this device');
      }

      // Request camera access with better error handling
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);
      
      // Additional mobile-specific handling
      if (videoRef.current) {
        videoRef.current.playsInline = true; // Important for iOS
        videoRef.current.muted = true; // Required for autoplay on mobile
        videoRef.current.setAttribute('playsinline', ''); // iOS Safari requirement
        videoRef.current.setAttribute('webkit-playsinline', ''); // Older iOS versions
      }
      
    } catch (err) {
      console.error('Camera access error:', err);
      
      // Provide specific error messages
      let errorMessage = "Unable to access camera.";
      if (err.name === 'NotAllowedError' || err.message.includes('denied')) {
        errorMessage = cameraAPI?.Capacitor.isNativePlatform() 
          ? "Camera permission denied. Please enable camera access in your device settings."
          : "Camera permission denied. Please enable camera access in your browser settings and reload the page.";
      } else if (err.name === 'NotFoundError') {
        errorMessage = "No camera found on this device.";
      } else if (err.name === 'NotSupportedError') {
        errorMessage = "Camera not supported on this device.";
      } else if (err.name === 'OverconstrainedError') {
        // Try again with basic constraints
        try {
          const basicStream = await navigator.mediaDevices.getUserMedia({ 
            video: { facingMode: 'environment' }, 
            audio: false 
          });
          setStream(basicStream);
          return; // Success with basic constraints
        } catch (basicErr) {
          errorMessage = "Camera constraints not supported. Try using a different device.";
        }
      }
      
      setPhotoError(errorMessage);
      setCameraActive(false);
    }
  };

  const capturePhoto = async () => {
    if (!cameraAPI) {
      setPhotoError('Camera not available');
      return;
    }

    try {
      // For native platforms, use Capacitor Camera
      if (cameraAPI.Capacitor.isNativePlatform()) {
        const photo = await cameraAPI.Camera.getPhoto({
          quality: 80,
          allowEditing: false,
          resultType: cameraAPI.CameraResultType.DataUrl,
          source: cameraAPI.CameraSource.Camera
        });
        
        setPhoto(photo.dataUrl);
        setCameraActive(false); // Close camera after capture
        return;
      }

      // Web fallback - existing canvas logic
      if (videoRef.current && canvasRef.current) {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        
        // Get actual video dimensions
        const videoWidth = video.videoWidth || video.clientWidth;
        const videoHeight = video.videoHeight || video.clientHeight;
        
        // Set canvas size to match video
        canvas.width = videoWidth;
        canvas.height = videoHeight;
        
        // Draw the video frame to canvas
        ctx.drawImage(video, 0, 0, videoWidth, videoHeight);
        
        // Convert to data URL with good quality
        const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
        setPhoto(dataUrl);
        
        // Stop camera after capture
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
          setCameraActive(false);
          setStream(null);
        }
      } else {
        setPhotoError("Camera not ready. Please try again.");
      }
    } catch (error) {
      console.error('Photo capture error:', error);
      setPhotoError("Failed to capture photo. Please try again.");
    }
  };

  return (
    <Dialog 
      className="complaint-dialog"
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      fullScreen={isMobile}
      sx={{
        '& .MuiDialog-paper': {
          padding: 0,
          margin: 0,
          overflow: 'hidden'
        },
        '& .MuiDialog-container': {
          padding: 0
        }
      }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '90%', md: '600px' },
          height: { xs: '100vh', sm: '80vh' },
          maxHeight: { xs: '100vh', sm: '85vh' },
          margin: { xs: 0, sm: 'auto' },
          borderRadius: { xs: 0, sm: 1 },
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          paddingBottom: 0,
          overflow: 'hidden',
          '&:after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '1px',
            backgroundColor: 'transparent'
          }
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          pb: 1,
          fontSize: { xs: '1.1rem', sm: '1.25rem' },
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          backgroundColor: 'background.paper',
          zIndex: 1
        }}
      >
        {initialData ? t('visitor.editVisitor') : t('visitor.addVisitor')}
      </DialogTitle>
      <DialogContent 
        sx={{ 
          p: { xs: 1.5, sm: 2 },
          overflowY: 'auto',
          flex: '1 1 auto',
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box 
          component="form" 
          onSubmit={handleSubmit} 
          sx={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 1.5, sm: 2 },
            flex: 1
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: { xs: 1, sm: 2 } }}>
              {error}
            </Alert>
          )}
          
          <TextField
            label={t('visitor.visitorName')}
            value={formData.visitorName}
            onChange={handleChange("visitorName")}
            fullWidth
            required
            margin="dense"
            disabled={!!initialData}
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: '14px', sm: '16px' }
              }
            }}
          />
          <TextField
            label={t('visitor.flat')}
            value={formData.flatId}
            onChange={handleChange("flatId")}
            fullWidth
            select
            required
            margin="dense"
            disabled={!!initialData}
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: '14px', sm: '16px' }
              }
            }}
          >
            {(() => {
              console.log("VisitorDialog: Rendering flats dropdown - flats array:", flats);
              console.log("VisitorDialog: Flats count:", (flats || []).length);
              if (!flats || flats.length === 0) {
                return (
                  <MenuItem disabled>
                    {t('visitor.noFlatsAvailable')}
                  </MenuItem>
                );
              }
              return (flats || []).map(f => {
                console.log("VisitorDialog: Rendering flat:", f);
                return (
                  <MenuItem key={f.flatId} value={f.flatId}>
                    {f.flatNumber} {f.blockName ? `(${f.blockName})` : ''}
                  </MenuItem>
                );
              });
            })()}
            {formData.flatId && !flats.some(f => f.flatId === formData.flatId) && (
              <MenuItem key={formData.flatId} value={formData.flatId}>
                {formData.flatNumber} {formData.blockName ? `(${formData.blockName})` : ''}
              </MenuItem>
            )}
          </TextField>
          <TextField
            select
            label={t('visitor.purpose')}
            value={formData.purpose}
            onChange={handleChange("purpose")}
            fullWidth
            required
            margin="dense"
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: '14px', sm: '16px' }
              }
            }}
          >
            <MenuItem value="Standard Visitors">{t('visitor.purposeStandardVisitors')}</MenuItem>
            <MenuItem value="Delivery Personnel">{t('visitor.purposeDeliveryPersonnel')}</MenuItem>
            <MenuItem value="Service Providers">{t('visitor.purposeServiceProviders')}</MenuItem>
            <MenuItem value="Temporary Residents">{t('visitor.purposeTemporaryResidents')}</MenuItem>
            <MenuItem value="Others">{t('visitor.purposeOthers')}</MenuItem>
          </TextField>
          <TextField
            label={t('visitor.phoneNumber')}
            value={formData.phoneNumber}
            onChange={handleChange("phoneNumber")}
            fullWidth
            margin="dense"
            type="tel"
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: '14px', sm: '16px' }
              }
            }}
          />
          <TextField
            label={t('visitor.checkInTime')}
            value={formData.inTime}
            onChange={handleChange("inTime")}
            fullWidth
            margin="dense"
            type="datetime-local"
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: '14px', sm: '16px' }
              }
            }}
          />
          <TextField
            label={t('visitor.status')}
            value={formData.status}
            onChange={handleChange("status")}
            fullWidth
            select
            margin="dense"
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: '14px', sm: '16px' }
              }
            }}
          >
            <MenuItem value="IN">{t('visitor.statusIn')}</MenuItem>
            <MenuItem value="OUT">{t('visitor.statusOut')}</MenuItem>
          </TextField>
          <TextField
            label={t('visitor.approvalStatus')}
            value={formData.approvalStatus}
            onChange={handleChange("approvalStatus")}
            fullWidth
            select
            margin="dense"
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: '14px', sm: '16px' }
              }
            }}
          >
            <MenuItem value="PENDING">{t('visitor.pending')}</MenuItem>
            <MenuItem value="APPROVED">{t('visitor.approved')}</MenuItem>
            <MenuItem value="REJECTED">{t('visitor.rejected')}</MenuItem>
            {canUseLeaveAtGate && <MenuItem value="Leave At Gate">{t('visitor.leaveAtGate')}</MenuItem>}
          </TextField>
          <TextField
            label={t('visitor.vehicleNumber')}
            value={formData.vehicleNumber}
            onChange={handleChange("vehicleNumber")}
            fullWidth
            margin="dense"
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: '14px', sm: '16px' }
              }
            }}
          />
          <TextField
            label={t('visitor.idType')}
            value={formData.idType}
            onChange={handleChange("idType")}
            fullWidth
            margin="dense"
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: '14px', sm: '16px' }
              }
            }}
          />
          <TextField
            label={t('visitor.idNumber')}
            value={formData.idNumber}
            onChange={handleChange("idNumber")}
            fullWidth
            margin="dense"
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: '14px', sm: '16px' }
              }
            }}
          />
          <TextField
            label={t('visitor.notes')}
            value={formData.notes}
            onChange={handleChange("notes")}
            fullWidth
            margin="dense"
            required={formData.purpose === "Others"}
            error={!!notesError}
            helperText={notesError}
            multiline
            rows={3}
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: '14px', sm: '16px' }
              }
            }}
          />

          {/* Camera and photo controls - only for building_admin and maintenance_staff */}
          {canUseCamera && (
            <Box sx={{ 
              mt: { xs: 1, sm: 2 },
              p: { xs: 1, sm: 2 },
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1
            }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontSize: { xs: '14px', sm: '16px' } }}>
                Visitor Photo
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1,
                mb: 2
              }}>
                {/* For native apps, show direct photo capture button */}
                {cameraAPI?.Capacitor.isNativePlatform() ? (
                  <Button 
                    variant="outlined" 
                    onClick={capturePhoto} 
                    disabled={!cameraInitialized}
                    size="small"
                    sx={{ fontSize: { xs: '12px', sm: '14px' } }}
                  >
                    {cameraInitialized ? 'Take Photo' : 'Initializing Camera...'}
                  </Button>
                ) : (
                  <>
                    <Button 
                      variant="outlined" 
                      onClick={startCamera} 
                      disabled={cameraActive || !cameraInitialized} 
                      size="small"
                      sx={{ fontSize: { xs: '12px', sm: '14px' } }}
                    >
                      {!cameraInitialized ? 'Initializing...' : cameraActive ? 'Camera Active' : 'Start Camera'}
                    </Button>
                    <Button 
                      variant="outlined" 
                      onClick={capturePhoto} 
                      disabled={!cameraActive || !cameraInitialized} 
                      size="small"
                      sx={{ fontSize: { xs: '12px', sm: '14px' } }}
                    >
                      Capture Photo
                    </Button>
                  </>
                )}
                {photo && (
                  <Button 
                    variant="outlined" 
                    color="error" 
                    onClick={() => setPhoto(null)}
                    size="small"
                    sx={{ fontSize: { xs: '12px', sm: '14px' } }}
                  >
                    Clear Photo
                  </Button>
                )}
              </Box>
              {photoError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {photoError}
                </Alert>
              )}
              {/* Camera preview - only for web browsers */}
              {!cameraAPI?.Capacitor.isNativePlatform() && cameraActive && (
                <Box sx={{ mt: 1 }}>
                  <video
                    ref={videoRef}
                    width="100%"
                    height="auto"
                    autoPlay
                    muted
                    playsInline
                    webkit-playsinline="true"
                    controls={false}
                    style={{ 
                      borderRadius: 4, 
                      border: "1px solid #e0e0e0",
                      maxWidth: window.innerWidth < 600 ? '100%' : '320px',
                      maxHeight: window.innerWidth < 600 ? '300px' : '240px',
                      objectFit: 'cover'
                    }}
                  />
                  <canvas ref={canvasRef} style={{ display: "none" }} />
                </Box>
              )}
              {/* Photo preview */}
              {photo && (
                <Box sx={{ mt: 1, textAlign: 'center' }}>
                  <img
                    src={typeof photo === "string" ? photo : ""}
                    alt="Visitor"
                    style={{ 
                      borderRadius: 4, 
                      border: "1px solid #e0e0e0",
                      maxWidth: '100%',
                      maxHeight: '160px',
                      width: 'auto',
                      height: 'auto'
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
          
          {/* Buttons at bottom of content */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 2 },
            justifyContent: 'center',
            mt: { xs: 2, sm: 3 },
            pt: { xs: 1.5, sm: 2 },
            borderTop: '1px solid #e0e0e0'
          }}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={isLoading}
              fullWidth={isMobile}
              size={isMobile ? "large" : "medium"}
              sx={{
                minHeight: { xs: '48px', sm: '40px' },
                fontSize: { xs: '16px', sm: '14px' },
                fontWeight: 'bold',
                borderRadius: '8px',
                order: { xs: 1, sm: 2 }
              }}
              onClick={handleSubmit}
            >
              {isLoading ? t('visitor.saving') : t('visitor.save')}
            </Button>
            
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={isLoading}
              fullWidth={isMobile}
              size={isMobile ? "large" : "medium"}
              sx={{
                minHeight: { xs: '48px', sm: '40px' },
                fontSize: { xs: '16px', sm: '14px' },
                borderRadius: '8px',
                order: { xs: 2, sm: 1 },
                color: '#666',
                borderColor: '#ccc',
                '&:hover': {
                  borderColor: '#999',
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              {t('visitor.cancel')}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default VisitorDashboard;